import { useHandler } from "@redotech/react-util/hook";
import { END_NODE_STEP } from "@redotech/redo-return-ui/flows/return-flow/end-node";
import { PHASE_ANCHOR_STEP } from "@redotech/redo-return-ui/flows/return-flow/phase-anchor";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Node, ReactFlowInstance } from "@xyflow/react";
import { SetStateAction, useEffect, useState } from "react";
import { updateStepConnection } from "../utils/palette/update-step-connection";
import { safeToModel } from "../utils/validation/safe-to-model";

const isEndNodeStep = (step: Step | undefined) =>
  step != null && safeToModel(step)?.type === END_NODE_STEP;

const isPhaseAnchorStep = (step: Step | undefined) =>
  step != null && safeToModel(step)?.type === PHASE_ANCHOR_STEP;

export interface ConnectionMode {
  sourceStepId: string;
  handleIndex: number;
}

export interface UseConnectionModeProps {
  reactFlowInstance: ReactFlowInstance | null;
  steps: Map<StepId, Step>;
  setStepWithId: (
    stepId: StepId,
    step: SetStateAction<Step | undefined>,
    options?: { skipHistory?: boolean },
  ) => void;
  setModified: (modified: boolean) => void;
  setSelectedId: (id: string | undefined) => void;
  clearSelectedNodeIds: () => void;
}

export interface UseConnectionModeReturn {
  connectionMode: ConnectionMode | null;
  mousePosition: { x: number; y: number } | null;
  hoveredNodeId: string | null;
  startConnectionMode: (sourceStepId: string, handleIndex: number) => void;
  finishConnectionMode: (targetStepId: string) => void;
  cancelConnectionMode: () => void;
  onNodeClick: (_: React.MouseEvent, node: Node) => void;
  onNodeMouseEnter: (_: React.MouseEvent, node: Node) => void;
  onNodeMouseLeave: () => void;
  onPaneClick: () => void;
}

export function useConnectionMode({
  reactFlowInstance,
  steps,
  setStepWithId,
  setModified,
  setSelectedId,
  clearSelectedNodeIds,
}: UseConnectionModeProps): UseConnectionModeReturn {
  const [connectionMode, setConnectionMode] = useState<ConnectionMode | null>(
    null,
  );
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const cancelConnectionMode = useHandler(() => {
    setConnectionMode(null);
    setMousePosition(null);
    setHoveredNodeId(null);
  });

  useEffect(() => {
    if (!connectionMode) {
      setMousePosition(null);
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!reactFlowInstance) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      setMousePosition(position);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [connectionMode, reactFlowInstance]);

  const startConnectionMode = useHandler(
    (sourceStepId: string, handleIndex: number) => {
      setConnectionMode({ sourceStepId, handleIndex });
    },
  );

  const finishConnectionMode = useHandler((targetStepId: string) => {
    if (!connectionMode) {
      return;
    }

    const sourceStep = steps.get(connectionMode.sourceStepId);
    if (!sourceStep) {
      cancelConnectionMode();
      return;
    }

    const updatedStep = updateStepConnection(
      sourceStep,
      connectionMode.handleIndex,
      targetStepId,
    );

    setStepWithId(connectionMode.sourceStepId, () => updatedStep);
    setModified(true);

    cancelConnectionMode();
  });

  const onNodeClick = useHandler((_: React.MouseEvent, node: Node) => {
    if (connectionMode) {
      finishConnectionMode(node.id);
      return;
    }

    // End nodes and phase anchors are inert markers — clicking them must not
    // open the inspector or change selection. End nodes have nothing to edit;
    // phase anchors are seeded one-per-phase and the Phase field is read-only.
    const clickedStep = steps.get(node.id);
    if (isEndNodeStep(clickedStep) || isPhaseAnchorStep(clickedStep)) {
      return;
    }

    setSelectedId(node.id);
    clearSelectedNodeIds();
  });

  const onNodeMouseEnter = useHandler((_: React.MouseEvent, node: Node) => {
    if (connectionMode) {
      setHoveredNodeId(node.id);
    }
  });

  const onNodeMouseLeave = useHandler(() => {
    if (connectionMode) {
      setHoveredNodeId(null);
    }
  });

  const onPaneClick = useHandler(() => {
    if (connectionMode) {
      cancelConnectionMode();
      return;
    }

    setSelectedId(undefined);
    clearSelectedNodeIds();
  });

  return {
    connectionMode,
    mousePosition,
    hoveredNodeId,
    startConnectionMode,
    finishConnectionMode,
    cancelConnectionMode,
    onNodeClick,
    onNodeMouseEnter,
    onNodeMouseLeave,
    onPaneClick,
  };
}
